import { useId } from 'react';

import { ColourRow } from './ColourRow';
import { DPad } from './DPad';
import { SizeDial } from './SizeDial';
import styles from './Controls.module.css';

interface Props {
  /** Board size as a percentage of the fitted picture — the "84%" in the mockups. */
  percent: number;
  /** How far through the size range the board is, 0–1. */
  fraction: number;
  /** Size range in stage px. */
  range: number;
  labelScale: number;
  lineColor: string;
  generating: boolean;
  onSizeDelta: (delta: number) => void;
  onNudge: (dx: number, dy: number) => void;
  onRecentre: () => void;
  onLine: (colour: string) => void;
  onLabelScale: (scale: number) => void;
  onGenerate: () => void;
  /** Shown on the mobile layout only. */
  showHeaderHint: boolean;
}

/** The editor controls — the mobile control sheet and the desktop sidebar share this. */
export function Controls({
  percent,
  fraction,
  range,
  labelScale,
  lineColor,
  generating,
  onSizeDelta,
  onNudge,
  onRecentre,
  onLine,
  onLabelScale,
  onGenerate,
  showHeaderHint,
}: Props): React.JSX.Element {
  const sliderId = useId();

  return (
    <div className={styles.controls}>
      <div className={styles.header}>
        <p className={styles.sectionTitle}>Line up the board</p>
        {showHeaderHint ? (
          <p className={styles.hint}>drag or pinch too</p>
        ) : (
          <p className={styles.blurb}>
            Drag the board on the picture, pull a corner, or scroll to resize.
          </p>
        )}
      </div>

      <div className={styles.dialRow}>
        <SizeDial percent={percent} fraction={fraction} onDelta={onSizeDelta} range={range} />
        <DPad onNudge={onNudge} onRecentre={onRecentre} />
      </div>

      <ColourRow value={lineColor} onChange={onLine} />

      <div className={styles.sliderGroup}>
        <div className={styles.sliderHead}>
          <label className={styles.label} htmlFor={sliderId}>
            Label size
          </label>
          <span className={styles.readout}>{labelScale.toFixed(1)}×</span>
        </div>
        <input
          id={sliderId}
          className={styles.range}
          type="range"
          min={0.7}
          max={2.4}
          step={0.05}
          value={labelScale}
          onChange={(event) => onLabelScale(Number(event.target.value))}
        />
      </div>

      <div className={styles.actions}>
        <button
          className="btn btn-primary btn-block"
          type="button"
          onClick={onGenerate}
          disabled={generating}
        >
          {generating ? 'Making the sheets…' : 'Make the two sheets'}
        </button>
        <p className={styles.caption}>9 × 9 · A–I across, 1–9 down</p>
      </div>
    </div>
  );
}

/** The 1·2·3 explainer shown in the desktop sidebar before a picture is loaded. */
export function HowItGoes(): React.JSX.Element {
  return (
    <div className={styles.placeholder}>
      <p className={styles.sectionTitle}>How it goes</p>
      <p className={styles.steps}>
        1 · Load a picture.
        <br />2 · Slide the 9 × 9 board over the good bit.
        <br />3 · Take two sheets — one blacked out for your players, one clean for you.
      </p>
    </div>
  );
}

/** The "Then" explainer shown in the mobile control sheet before a picture is loaded. */
export function ThenExplainer(): React.JSX.Element {
  return (
    <div className={styles.placeholder}>
      <p className={styles.sectionTitle}>Then</p>
      <p className={styles.steps}>
        Slide the 9 × 9 board over the good bit of the picture, then make two sheets: one
        blacked out for your players, one clean for you.
      </p>
    </div>
  );
}
